<template>
  <div class="mt-4">
    <div class="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
      <h2 class="text-xl font-bold mb-6 text-gray-800">View Results</h2>

      <div class="mb-8 flex flex-wrap gap-4 items-end">
        <div v-if="isTertiary && programmeOptions.length > 1" class="w-64">
          <label class="block text-sm font-medium text-gray-700 mb-1">Programme</label>
          <select v-model="selectedProgramme" class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary">
            <option v-for="opt in programmeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>
        </div>
        <div class="flex-1 min-w-[200px]">
          <label class="block text-sm font-medium text-gray-700 mb-1">Select {{ labels.TERM }}</label>
          <ReusablePrimeFilter :options="termOptions" :title="'Select ' + labels.TERM" :selected="selectedTerm"
            @select="onTermSelect" />
        </div>
      </div>

      <!-- All Semesters View (triggered by the filter's built-in "All" option) -->
      <AllSemestersView v-if="!selectedTerm && studentId" :studentId="String(studentId)" :programmeId="selectedProgramme" />

      <!-- Loading State -->
      <div v-else-if="loading" class="mt-8 text-center text-gray-500">
        <i class="pi pi-spinner pi-spin text-2xl"></i>
        <p>Loading results...</p>
      </div>

      <!-- Provisional CA Result -->
      <div v-else-if="isProvisionalCA && resultData" class="mt-8">
        <div class="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center gap-3">
          <i class="pi pi-info-circle text-amber-600 text-xl"></i>
          <div>
            <p class="text-sm font-semibold text-amber-800">Provisional CA Result</p>
            <p class="text-xs text-amber-700">Exam scores and GPA will be available after senate approval. Only approved CA scores are shown below.</p>
          </div>
        </div>

        <div class="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
          <table class="w-full text-sm text-left">
            <thead class="bg-gray-50 text-gray-500 font-semibold uppercase tracking-wider text-xs border-b border-gray-200">
              <tr>
                <th v-for="header in tableHeaders" :key="header" class="px-6 py-4 whitespace-nowrap">{{ header }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="(subject, idx) in subjects" :key="idx" class="hover:bg-gray-50/50">
                <td v-for="(header, hIdx) in tableHeaders" :key="hIdx"
                  class="px-6 py-4 whitespace-nowrap text-gray-700"
                  :class="{ 'font-medium text-gray-900': hIdx === 0 }">
                  {{ subject[header] ?? '-' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mt-4 flex justify-center">
          <button type="button" @click="downloadPdf" :disabled="downloading"
            class="w-48 bg-amber-500 p-3 text-white rounded-md text-center hover:bg-amber-600 disabled:opacity-50 text-sm">
            {{ downloading ? 'Downloading...' : 'Download Provisional PDF' }}
          </button>
        </div>
      </div>

      <!-- Error State — No result for this calendar -->
     <div v-else-if="error" class="mt-8">
        <div class="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="h-1.5 w-full bg-gradient-to-r from-amber-400 via-orange-400 to-red-400"></div>
          <div class="flex flex-col items-center justify-center py-16 px-8 text-center">
            <div class="w-20 h-20 rounded-full bg-orange-50 flex items-center justify-center mb-6">
              <i class="pi pi-file-excel text-3xl text-orange-400"></i>
            </div>
            <h3 class="text-xl font-bold text-gray-800 mb-2">No Results Available</h3>
            <p class="text-gray-500 text-sm max-w-md leading-relaxed">{{ errorMessage }}</p>
            <p class="text-gray-400 text-xs mt-4">
              If you believe this is an error, please contact your school administration.
            </p>
          </div>
        </div>
      </div>

      <!-- Result Not Published Yet -->
      <div v-else-if="resultData && !hasViewableResult" class="mt-8">
        <div class="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="h-1.5 w-full bg-gradient-to-r from-primary via-indigo-400 to-purple-400"></div>
          <div class="flex flex-col items-center justify-center py-16 px-8 text-center">
            <div class="w-20 h-20 rounded-full bg-indigo-50 flex items-center justify-center mb-6">
              <i class="pi pi-clock text-3xl text-indigo-400"></i>
            </div>
            <h3 class="text-xl font-bold text-gray-800 mb-2">Results Not Published Yet</h3>
            <p class="text-gray-500 text-sm max-w-md leading-relaxed">
              Your results for this semester have been compiled but have not been officially published by your school yet. Please check back later.
            </p>
            <div class="mt-6 inline-flex items-center gap-2 bg-indigo-50 text-indigo-600 px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider border border-indigo-100">
              <i class="pi pi-info-circle"></i>
              Awaiting Publication
            </div>
          </div>
        </div>
      </div>

      <!-- Report Card -->
      <div v-else-if="resultData && hasViewableResult" class="mt-8">

        <article
          class="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden relative print:shadow-none print:border-none">
          <!-- Decorative Header Bar -->
          <div class="h-2 w-full bg-gradient-to-r from-primary/50 via-emerald-500 to-teal-500"></div>

          <!-- Header Section -->
          <header class="p-8 md:p-12 border-b border-gray-100 flex flex-col md:flex-row items-center gap-8">
            <img v-if="schoolLogo" loading="lazy" :src="schoolLogo" class="object-contain aspect-square w-[92px] rounded-lg" alt="School logo" />
            <div class="flex-1 text-center md:text-left">
              <h1 class="text-3xl font-bold text-gray-900 tracking-tight">{{ schoolName }}</h1>
              <p class="text-gray-500 mt-2 text-sm leading-relaxed max-w-lg mx-auto md:mx-0">
                {{ schoolAddress }}
              </p>
              <div
                class="flex flex-wrap justify-center md:justify-start gap-4 mt-4 text-xs font-medium text-gray-400 uppercase tracking-wider">
                <span v-if="schoolWeb" class="flex items-center gap-1"><i class="pi pi-globe"></i> {{
                  schoolWeb
                }}</span>
                <span v-if="schoolEmail" class="flex items-center gap-1"><i class="pi pi-envelope"></i> {{
                  schoolEmail
                }}</span>
                <span v-if="schoolMobile" class="flex items-center gap-1"><i class="pi pi-phone"></i> {{ schoolMobile
                }}</span>
              </div>
            </div>
            <div class="hidden md:block text-right">
              <div
                class="bg-emerald-50 text-emerald-700 px-6 py-2 rounded-full font-bold text-sm tracking-wide border border-emerald-100 shadow-sm">
                {{ layout.documentTitle }}
                <span v-if="isProvisionalCA"
                  class="ml-2 text-orange-500 text-xs border border-orange-500 px-1 rounded">PROVISIONAL</span>
              </div>
            </div>
          </header>

          <!--Student & Term Details-->
          <section class="bg-gray-50/50 p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-10">
            <!--Student Profile Actions-->
            <div class="flex items-start gap-5">
              <div
                class="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 text-2xl font-bold">
                {{ studentName.charAt(0) }}
              </div>
              <div>
                <h2 class="text-2xl font-bold text-gray-800">{{ studentName }}</h2>
                <p class="text-gray-500 text-sm font-medium mt-1">{{ studentClass }}</p>
                <div class="mt-4 grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                  <div class="text-gray-400">{{ layout.idLabel }}</div>
                  <div class="font-semibold text-gray-700">{{ leftColumnInfo['Admission Number'] }}</div>
                  <div class="text-gray-400">Gender</div>
                  <div class="font-semibold text-gray-700 capitalize">{{ leftColumnInfo['Gender'] }}</div>
                </div>
              </div>
            </div>

            <!-- Term & Attendance -->
            <div class="grid grid-cols-2 gap-6 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <div>
                <div class="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-3">Academic Info</div>
                <div class="space-y-2">
                  <div>
                    <span class="text-gray-500 text-xs block">Session</span>
                    <span class="text-sm font-semibold text-gray-800">{{ leftColumnInfo['Academic Session'] }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 text-xs block">{{ labels.TERM }}</span>
                    <span class="text-sm font-semibold text-gray-800">{{ leftColumnInfo['Term'] }}</span>
                  </div>
                </div>
              </div>
              <div v-if="layout.showAttendance">
                <div class="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-3">Attendance</div>
                <div class="space-y-1 text-sm">
                  <div class="flex justify-between">
                    <span class="text-gray-500">Opened</span>
                    <span class="font-medium text-gray-900">{{ rightColumnInfo['No of times school opened'] }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-500">Present</span>
                    <span class="font-medium text-emerald-600">{{ rightColumnInfo['No of times present'] }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-500">Absent</span>
                    <span class="font-medium text-red-500">{{ rightColumnInfo['No of times absent'] }}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Result Table -->
          <section class="p-8 md:p-10">
            <div class="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
              <table class="w-full text-sm text-left">
                <thead
                  class="bg-gray-50 text-gray-500 font-semibold uppercase tracking-wider text-xs border-b border-gray-200">
                  <tr>
                    <th v-for="header in tableHeaders" :key="header" class="px-6 py-4 whitespace-nowrap">
                      {{ header === 'Subject' ? layout.courseLabel : header }}
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                  <template v-for="(subject, idx) in subjects" :key="idx">
                  <tr v-if="moduleHeadingAt(idx)"><td :colspan="tableHeaders.length" class="px-6 py-3 font-semibold bg-gray-100">{{ moduleHeadingAt(idx) }}</td></tr>
                  <tr class="hover:bg-gray-50/50 transition-colors">
                    <td v-for="(header, hIdx) in tableHeaders" :key="hIdx"
                      class="px-6 py-4 whitespace-nowrap text-gray-700"
                      :class="{ 'font-medium text-gray-900': hIdx === 0 }">
                      {{ subject[header] }}
                    </td>
                  </tr>
                  </template>
                  <tr v-if="subjects.length === 0">
                    <td :colspan="tableHeaders.length" class="px-6 py-12 text-center text-gray-400">
                      No result data available.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- Remarks & Grading -->
          <section class="grid grid-cols-1 gap-8 p-8 md:p-10 border-t border-gray-100 bg-gray-50/30">

            <!-- Side-by-side tables for Dynamic Categories -->
            <div v-if="Object.keys(categorizedFeedbacks.groups).length" class="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
              <div v-for="(groupFeedbacks, categoryName, index) in categorizedFeedbacks.groups" :key="categoryName">
                <h3 class="text-xs font-bold uppercase tracking-wider mb-4"
                    :class="index % 2 === 0 ? 'text-primary' : 'text-purple-600'">[ {{ categoryName }} ]</h3>
                <div class="bg-white rounded-lg shadow-sm border border-gray-300 overflow-hidden w-fit">
                  <table class="border-collapse">
                    <tbody>
                      <tr v-for="(fb, i) in groupFeedbacks" :key="fb.id"
                        :class="i !== groupFeedbacks.length - 1 ? 'border-b border-gray-300' : ''">
                        <td class="p-3 text-sm text-gray-700 border-r border-gray-300">{{ fb.label }}</td>
                        <td class="p-3 text-sm font-bold text-center w-16"
                            :class="index % 2 === 0 ? 'text-primary' : 'text-purple-600'">{{ fb.value || '-' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- GPA/CGPA Summary, Remarks, Grade Key -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-8">

              <!-- GPA/CGPA Summary (tertiary only) -->
              <div v-if="layout.showGpaSummary" class="md:col-span-3">
                <div class="flex items-center gap-6 p-4 bg-primary/5 rounded-xl border border-primary/10">
                  <div v-for="(item, key) in summaryItems" :key="key" class="text-center">
                    <div class="text-xs text-gray-500 uppercase tracking-wider font-semibold">{{ item.label }}</div>
                    <div class="text-lg font-bold text-gray-900 mt-1">{{ item.value ?? 'N/A' }}</div>
                  </div>
                </div>
              </div>

              <!-- Comments (non-tertiary only) -->
              <div v-if="layout.showRemarks" class="md:col-span-2 space-y-6">

                <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 relative">
                  <div class="absolute top-0 left-0 w-1 p-0.5 h-full rounded-l-2xl bg-indigo-500"></div>
                  <h4 class="text-sm font-bold text-indigo-900 uppercase tracking-wide mb-2">{{ labels.TEACHER }}'s Remark</h4>
                  <p class="text-gray-600 italic leading-relaxed">"{{ teacherComment }}"</p>
                </div>

                <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 relative">
                  <div class="absolute top-0 left-0 w-1 p-0.5 h-full rounded-l-2xl bg-teal-500"></div>
                  <h4 class="text-sm font-bold text-teal-900 uppercase tracking-wide mb-2">Principal's Remark</h4>
                  <p class="text-gray-600 italic leading-relaxed">"{{ principalComment }}"</p>
                </div>

              </div>

              <!-- Grade Key -->
              <div>
                <h3 class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Grading System</h3>
                <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div v-for="grade in grades" :key="grade.letter"
                    class="flex border-b border-gray-50 last:border-none p-3 text-sm">
                    <div class="w-12 font-bold text-center text-emerald-600 bg-emerald-50 rounded-md py-1">{{ grade.letter
                      }}
                    </div>
                    <div class="flex-1 text-right text-gray-600 self-center">{{ grade.range }}</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Next Term/Semester -->
          <footer v-if="layout.showNextTerm" class="bg-emerald-900 text-white p-8 text-center">
            <p class="text-emerald-200 text-xs font-bold uppercase tracking-widest mb-1">Important</p>
            <h3 class="text-lg md:text-xl font-medium">Next Term Resumes: <span class="font-bold text-white">{{
              nextTermDate || 'To be announced'
                }}</span></h3>
          </footer>

        </article>
      </div>

      <!-- Empty State -->
      <div v-else-if="!loading && !resultData"
        class="mt-8 text-center text-gray-500 py-10 border-2 border-dashed rounded-lg">
        <p>Please select a semester to view results.</p>
      </div>

      <div v-if="resultData && hasViewableResult" class="flex justify-center items-center mt-4">
        <button type="button" @click="downloadPdf" :disabled="downloading"
          class="w-40 bg-primary p-4 text-white rounded-md whitespace-nowrap text-center hover:bg-primary/90 disabled:opacity-50">
          {{ downloading ? 'Downloading...' : 'Download PDF' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ReusablePrimeFilter from '@/components/common/ReusablePrimeFilter.vue';
import AllSemestersView from './AllSemestersView.vue';
import { formatSummaryItems } from './resultSummary';
import { useUserQuery } from '@/composables/queries/useUserQuery';
import { useStudentTermOptions } from '@/composables/useStudentTermOptions';
import { GET_STUDENT_REGISTERED_CALENDARS, GET_STUDENT_ENROLLED_PROGRAMMES } from '@/graphql/Queries/index';
import { useSchoolLabels } from '@/composables/useSchoolLabels';

const { labels } = useSchoolLabels();
import { useApolloClient } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import { DOWNLOAD_STUDENT_RESULT } from '@/graphql';
import { useResultProcessing } from '@/composables/useResultProcessing';
import { useStudentResultQuery } from '@/composables/queries/useStudentResultQuery';
import { useReportCardUtils } from '@/composables/useReportCardUtils';
import { ResultStatus, type AcademicCalendar, type Maybe } from '@/types/composition-functions';

const toast = useToast();

// User Profile Data
const { studentProfile } = useUserQuery();

const isTertiary = computed(() =>
  studentProfile.value?.currentClassroomSection?.school?.school_type?.toLowerCase() === 'tertiary',
);

const schoolInfo = computed(() =>
  studentProfile.value?.currentClassroomSection?.school || {},
);

const schoolName = computed(() => schoolInfo.value.name);
const schoolAddress = computed(() => schoolInfo.value.address);
const schoolEmail = computed(() => schoolInfo.value.email);
const schoolWeb = computed(() => schoolInfo.value.website);
const schoolMobile = computed(() => schoolInfo.value.mobile);
const schoolLogo = computed(() => schoolInfo.value.logo);

// Filters — programme selector (multi-programme tertiary students) plus term
// options built from the calendars the student actually registered courses in.
const selectedProgramme = ref<string>();

const { resolveClient } = useApolloClient();

interface EnrolledProgramme {
  id: string | number;
  program_name?: string | null;
  degree?: string | null;
  is_active_programme?: boolean | null;
}

const enrolledProgrammes = ref<EnrolledProgramme[]>([]);

const programmeOptions = computed(() =>
  enrolledProgrammes.value.map((p) => ({
    label: p.degree ? `${p.program_name} (${p.degree})` : p.program_name,
    value: String(p.id),
  })),
);

watch(() => studentProfile.value?.id, async(id) => {
  if (!id) return;
  try {
    const client = resolveClient();
    const { data } = await client.query({
      query: GET_STUDENT_ENROLLED_PROGRAMMES,
      variables: { student_id: String(id) },
      fetchPolicy: 'network-only',
    });
    enrolledProgrammes.value = data?.studentEnrolledProgrammes || [];

    // Default to the programme tied to the active calendar
    const activeProg = enrolledProgrammes.value.find((p) => p.is_active_programme);
    if (activeProg) {
      selectedProgramme.value = String(activeProg.id);
    } else if (enrolledProgrammes.value.length > 0) {
      selectedProgramme.value = String(enrolledProgrammes.value[0]?.id ?? '');
    }
  } catch {
    enrolledProgrammes.value = [];
  }
}, { immediate: true });

// Calendars the student has course registrations for, filtered by programme.
// Fed through useStudentTermOptions so ordering, the active-calendar default
// and the fallback to the most recent registered calendar match the rest of
// the student portal (a school-wide active calendar the student never
// registered in must not be auto-selected).
const registeredCalendars = ref<Maybe<AcademicCalendar>[]>([]);

async function fetchCalendars() {
  const id = studentProfile.value?.id;
  if (!id) return;
  try {
    const client = resolveClient();
    const variables: Record<string, string> = { student_id: String(id) };
    if (selectedProgramme.value) {
      variables.programme_id = selectedProgramme.value;
    }
    const { data } = await client.query({
      query: GET_STUDENT_REGISTERED_CALENDARS,
      variables,
      fetchPolicy: 'network-only',
    });
    registeredCalendars.value = data?.studentRegisteredCalendars || [];
  } catch {
    registeredCalendars.value = [];
  }
}

watch(() => studentProfile.value?.id, () => fetchCalendars(), { immediate: true });

const { calendars, termOptions, selectedTerm, onTermSelect: baseTermSelect } = useStudentTermOptions(registeredCalendars);

// Switching programme clears the term; the composable re-selects the active
// (or most recent) calendar once the new list arrives.
watch(selectedProgramme, () => {
  baseTermSelect(undefined);
  fetchCalendars();
});

function onTermSelect(value: string | undefined) {
  baseTermSelect(value);
  if (value) {
    nextTick(() => refetchResult?.());
  }
}

// Fetch Result Query
const studentId = computed(() => studentProfile.value?.id);
const enabledQuery = computed(() => !!studentId.value && !!selectedTerm.value);

const {
  studentResult,
  attendanceResult,
  currentSessionData,
  loadingResult,
  loadingAttendance,
  errorResult,
  refetchResult,
} = useStudentResultQuery(studentId, selectedTerm, enabledQuery);

const resultData = computed(() => studentResult.value);
const loading = computed(() => loadingResult.value || loadingAttendance.value);
const error = computed(() => errorResult.value);
const isPublished = computed(() => studentResult.value?.status === ResultStatus.Published);
const isProvisionalCA = computed(() => studentResult.value?.result_mode === 'PROVISIONAL_CA');
const hasViewableResult = computed(() => isPublished.value);

// Template-driven layout config — schools control what appears on the result
const layout = computed(() => {
  const templateProps = studentResult.value?.template?.properties;
  const cfg = templateProps?.layout || {};
  return {
    documentTitle: cfg.document_title || (isTertiary.value ? 'SEMESTER RESULT' : 'REPORT CARD'),
    idLabel: cfg.id_label || (isTertiary.value ? 'Matric No' : 'Admission No'),
    courseLabel: cfg.course_label || (isTertiary.value ? 'Course' : 'Subject'),
    showAttendance: cfg.show_attendance ?? !isTertiary.value,
    showRemarks: cfg.show_remarks ?? !isTertiary.value,
    showNextTerm: cfg.show_next_term ?? !isTertiary.value,
    showGpaSummary: cfg.show_gpa_summary ?? isTertiary.value,
    showGradingKey: cfg.show_grading_key ?? true,
    showFeedbacks: cfg.show_feedbacks ?? !isTertiary.value,
  };
});

const summaryItems = computed(() => {
  if (!layout.value.showGpaSummary) return [];

  return formatSummaryItems(studentResult.value?.summary);
});

const { grades, calculateGrade, tableHeaders, processedSubjects: subjects, moduleHeadingAt } = useResultProcessing(studentResult);

const {
  studentName,
  studentClass,
  leftColumnInfo,
  rightColumnInfo,
  teacherComment,
  principalComment,
  nextTermDate,
  categorizedFeedbacks,
} = useReportCardUtils(studentResult, attendanceResult, currentSessionData, calendars, selectedTerm);

const downloading = ref(false);

async function downloadPdf() {
  if (!studentId.value || !selectedTerm.value) return;
  downloading.value = true;
  try {
    const client = resolveClient();
    const { data } = await client.query({
      query: DOWNLOAD_STUDENT_RESULT,
      variables: {
        student_id: studentId.value,
        academic_calendar_id: selectedTerm.value,
      },
      fetchPolicy: 'no-cache',
    });
    if (!data?.downloadStudentResult?.id) {
      toast.add({ severity: 'warn', summary: 'Could not start export', detail: 'Please try again.', life: 4000 });
      return;
    }
    toast.add({
      severity: 'success',
      summary: 'Export Started',
      detail: 'Your result PDF is being generated. Check the Downloads icon in the top bar for progress.',
      life: 5000,
    });
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Download Failed', detail: 'Could not start the result PDF export.', life: 4000 });
  } finally {
    downloading.value = false;
  }
}

const errorMessage = computed(() => {
  const message = error.value?.message ?? '';

  if (
    message.includes('No result template assigned') ||
    message.includes('not enrolled')
  ) {
    return 'Results are not yet available for this semester. Your school may still be preparing them.';
  }

  if (message.includes('Student is not enrolled')) {
    return 'You are not enrolled in this academic calendar. Please contact your school administrator.';
  }

  return 'We could not load your results for this semester. Please try again or contact your school administration.';
});

</script>

<style scoped>
@media print {
  button {
    display: none;
  }
}
</style>
